import { TitleCasePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OnboardingMenu } from 'src/app/enums/enums';
import { CompanyAttachmentService } from 'src/app/services/core/company-attachment/company-attachment.service';
import { UploadService } from 'src/app/services/core/documents/upload.service';
import { LosService } from 'src/app/services/core/levelOfService/los.service';
import { validateFileSize } from 'src/app/utils/common.utils';
import { ImageSnippet, imageProcessing } from 'src/app/utils/image-processing-utils';
import { sweetAlert } from 'src/app/utils/swal';
import { urls } from 'src/app/utils/url.utils';

@UntilDestroy()
@Component({
  selector: 'app-upload-attachment',
  templateUrl: './upload-attachment.component.html',
  styleUrls: ['./upload-attachment.component.css']
})
export class UploadAttachmentComponent implements OnInit {
  attachmentForm!: FormGroup;
  attachmentId: any;
  submitted = false;
  companyId: any;
  existingDocumentTypes: string[] = [];

  // selectedFile: ImageSnippet | null = null;
  // profileImageURL: string | null = null;

  // Edit Modal Inputs and Outputs
  @Input() attachment: any;
  @Input() source: any;
  @Output() onSaveAttachment = new EventEmitter<string>();
  @Output() onCloseModal = new EventEmitter<string>();
  // Edit Modal Inputs and Outputs End

  // documentTypes = [
  //   'Contract',
  //   'W-9',
  //   'Rates Sheet',
  //   'Disclosure Of Ownership',
  //   'Non Emergency Account Setup',
  //   'Provider Questionnaire',
  //   'Dwmbe Questionnaire',
  //   'After Hours Contact Sheet',
  //   'Provider Manual Acknowledgement',
  //   'Other'
  // ];

  documentTypes = [
    'contract',
    'w-9',
    'rates sheet',
    'disclosure of ownership',
    'non emergency account setup',
    'provider questionnaire',
    'dwmbe questionnaire',
    'after hours contact sheet',
    'provider manual acknowledgement',
    'other'
  ];

  selectedDocument = '';

  selectedFile: File | null = null
  invalidFileType = false;


  constructor(
    private fb: FormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private uploadService: UploadService,
    private attachmentService: CompanyAttachmentService
  ) { }

  ngOnInit(): void {
    if (this.source === 'modal' && this.attachment) {
      this.attachmentId = this.attachment.id;
      this.getById();
    }

    this.companyId = this.activatedRoute.snapshot.params['companyId'];

    this.attachmentForm = this.fb.group({
      documentType: ["", Validators.required],
      title: [""],
      documentUrl: ["", Validators.required],
      company: [""],
    });

    // this.attachmentForm.patchValue({
    //   documentUrl: "https://us-lax-1.linodeobjects.com/broker-assets-uploads/attchments/ea79195e-543e-4df2-b9c7-f6e1c6bfa833-pdf"
    // });

    if (this.companyId) {
      this.attachmentForm.patchValue({
        company: this.companyId
      });
      this.getExistingAttachments();
    }
  }

  getExistingAttachments() {
    this.attachmentService.getCompanyAttachments(this.companyId, { limit: 1000 }).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data && data.results) {
        this.existingDocumentTypes = data.results
          .filter((att: any) => att.id !== this.attachmentId)
          .map((att: any) => att.title?.toLowerCase());
      }
    });
  }

  onChangeDocument() {
    const documentType = this.attachmentForm.get('documentType')?.value;
    this.attachmentForm.patchValue({
        title: documentType && documentType !== 'other' ? documentType : ''
    });

    if (documentType === 'other') {
      this.attachmentForm.get('title')?.setValidators([Validators.required]);
    } else {
      this.attachmentForm.get('title')?.clearValidators();
    }

    this.attachmentForm.get('title')?.updateValueAndValidity();
  }

  onSubmit() {
    this.submitted = true;
    if (this.attachmentForm.invalid) {
      return;
    }

    const payload = this.attachmentForm.value
    delete payload.documentType;

    this.attachmentService.save(this.attachmentId, payload).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) {
        if (this.source === 'modal') {
          this.onSaveAttachment.emit(data);
        } else {
          this.router.navigate(['/credentials', this.companyId], { fragment: OnboardingMenu.ATTACHMENTS });
        }
      }
    }, (err) => {
      sweetAlert("Error", err, "error", "OK");
    });
  }

  getById() {
    this.attachmentService.getById(this.attachmentId).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) {
        this.attachmentForm.patchValue({
          ...data,
          documentType: data.title
        });
        if (!this.documentTypes.includes(data.title)) {
          this.attachmentForm.patchValue({
          documentType: 'other',
          title: new TitleCasePipe().transform(data.title)
        });
        }
      }
    })
  }

  onFileSelection(event: any): void {
    const file: File = event.target?.files?.[0];

    if (!file) {
      this.selectedFile = null;
      return;
    }

    const allowedMimeTypes = [
      "application/pdf" //.pdf
    ];

    const allowedExtensions = ["pdf"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    const isValidType =
      allowedMimeTypes.includes(file.type) ||
      (fileExtension && allowedExtensions.includes(fileExtension));

    if (!isValidType) {
      this.selectedFile = null;
      this.invalidFileType = true;
      sweetAlert(
        "Error",
        "Invalid file type. Only PDF files are allowed.",
        "error",
        "OK"
      );

      return;
    }

    this.selectedFile = file;
    this.invalidFileType = false;

    this.uploadService.uploadImageToServer(this.selectedFile, urls.COMPANIES).pipe(untilDestroyed(this)).subscribe({
      next: (uploadResult: any) => {
        if (uploadResult?.fileUrl) {
          this.attachmentForm.get('documentUrl')?.setValue(uploadResult.fileUrl);
        }
      },
    });

  }

  deleteFile(fileInput: HTMLInputElement | null) {
    this.selectedFile = null;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  get form() {
    return this.attachmentForm.controls;
  }
}

